import { createFileRoute, getRouteApi, Link, notFound } from "@tanstack/react-router";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { initializeWorker, forwardAuthorization } from "../../storageWorker/handles.js";
import listItemColumns from '../models/listItem';
import { messages } from '../../storageWorker/messages.js';
import Pagination from '../components/Pagination.jsx';
import Pocketbase from 'pocketbase';

// Icon files
import objectImage from '../../../images/new-icons/Icon_240_art.svg';
import archiveImage from '../../../images/new-icons/Icon_240_archives.svg';
import libraryImage from '../../../images/new-icons/Icon_240_books.svg';
import exhibitionImage from '../../../images/new-icons/Icon_240_exhibition.svg';
import actorImage from '../../../images/new-icons/Icon_240_peopleorgs.svg';
import personalImage from '../../../images/new-icons/Icon_240_belongings.svg';
import materialsImage from '../../../images/new-icons/Icon_240_materials.svg';
import noneImage from '../../../images/no-img-thumb-242.png';
import SortControl from "../components/SortControl.jsx";
import { PDFPrinter, DataPrinter } from "../printer/index.js";
import ExportModal from "../components/ExportModal.jsx";
import ShareModal from "../components/ShareModal.jsx";
import UnshareModal from "../components/UnshareModal.jsx";
const backend = 'https://access-okeeffe.pockethost.io';
const DEFAULT_PAGE_SIZE = 100;
const placeholderImage = data => {
  const {
    type
  } = data;
  switch (true) {
    case type === 'library':
      return '/assets/' + libraryImage;
    case type === 'object':
      return '/assets/' + objectImage;
    case type === 'exhibition':
    case type === 'exhibition_venue':
      return '/assets/' + exhibitionImage;
    case type === 'actor':
      return '/assets/' + actorImage;
    case type === 'archive':
      return '/assets/' + archiveImage;
    default:
      return "/assets/images/no-img-thumb-242.png";

    // TODO: Handle personal and materials
    // case type === 'personal':
    //   return `/assets/svg/Icon_${size}_belongings.svg`

    // case result._source.type === 'materials':
    //   return `/assets/svg/Icon_${size}_materials.svg`
  }
};
const parsePageParams = search => {
  const params = new URLSearchParams(search);

  // Reduce array of objects to object of array params
  return params.entries().reduce((accumParams, [curKey, curVal]) => {
    switch (true) {
      // Unique params
      case curKey === 'listId':
        accumParams[curKey] = curVal;
      // Array params
      case curKey in accumParams:
        accumParams[curKey].push(curVal);
      default:
        accumParams[curKey] = [curVal];
    }
    return accumParams;
  }, {});
};
const DEFAULT_SORT_FIELDS = ['Date Added', 'Title', 'Date', 'Maker', 'Type'];
const ListItemRow = (row, index, removeItem) => {
  const cells = row.getVisibleCells();
  const visibleValues = cells.reduce((acc, cur) => {
    const key = cur.column.id;
    acc[key] = cur.getValue();
    return acc;
  }, {});
  const {
    id,
    collection,
    date,
    imageUrl,
    maker,
    title,
    cr_data,
    credit_line,
    date_start,
    date_end,
    dimensions,
    materials,
    description
  } = visibleValues;
  let src = placeholderImage(visibleValues);
  if (imageUrl) {
    const terminated = imageUrl?.endsWith('/') ? imageUrl : imageUrl + '/';
    src = new URL('full/,242/0/default.jpg', terminated).href;
  }
  return <li className="list-item" key={index}>
    <div className="columns is-flex-mobile is-vcentered">
      <div className="column is-11 is-10-mobile">
        <div className="columns">
          <div className="column is-one-fifth is-12-mobile list-item-image">
            <figure className="image">
              <img className="" src={src} alt="" loading={index > 3 ? 'eager' : 'lazy'} />
            </figure>
          </div>
          <div className="column is-12-mobile d-none-print">
            <div className="list-item-text">{maker}</div>
            <div className="list-item-description">
              <a href={'/' + id} className="btn-text btn-lg px-0 mr-3">{title}</a>
            </div>
            <div className="list-item-date">{date}</div>
            <div className="list-item-text">{collection}</div>
          </div>
          <div className="column d-block-print">
            <div className="columns">
              <div className="column">
                <div className="list-item-text">{maker}</div>
                <div className="list-item-description">
                  <span className="">{title}</span>
                  <span className="list-item-date">, {date}</span>
                </div>
                <div className="list-item-description">{materials}, {dimensions}</div>
                <div className={`list-item-description ${!Boolean(cr_data?.cr_number) ? 'is-hidden' : ''}`}>CR No. {cr_data?.cr_number}</div>
              </div>
              <div className="column">
                <div className="list-item-text">{collection}</div>
                <div className="list-item-description">{credit_line}</div>
                {/* TODO: Provenance and accession # */}
                <div className="list-item-description is-hidden">December 1984 (1984.12.28.1)</div>
              </div>
            </div>
            <p className="list-item-description">{description}</p>
            {/* TODO: image download status */}
            <p className="list-item-description is-hidden">No image download available</p>
          </div>
        </div>
      </div>
      <div className="column is-justify-content-flex-end">

        <div className="is-right">
          <button type="button" className="btn-icon-only icon-lg btn-close" onClick={() => removeItem(id)}><span className="icon icon-close-large"></span><span className="sr-only">Remove from list</span></button>
        </div>
      </div>
    </div>
  </li>;
};
const requestInitialData = async (port, pb, listId) => {
  const list = await new Promise((resolve, reject) => {
    const handleRequest = event => {
      const {
        data: eventData
      } = event;
      const {
        type,
        data
      } = eventData;
      if (type === messages.LOAD_LIST) {
        port.removeEventListener('message', handleRequest);
        resolve(data);
      } else {
        reject("Did not receive expected backend response!");
      }
    };
    port.onmessage = handleRequest;
    port.postMessage({
      type: messages.LOAD_LIST,
      id: listId
    });
  });
  const count = await new Promise((resolve, reject) => {
    const handleRequest = event => {
      const {
        data: eventData
      } = event;
      const {
        type,
        data
      } = eventData;
      if (type === messages.COUNT_LIST_ITEMS) {
        port.removeEventListener('message', handleRequest);
        resolve(data);
      } else {
        reject("Did not receive expected backend response!");
      }
    };
    port.onmessage = handleRequest;
    port.postMessage({
      type: messages.COUNT_LIST_ITEMS,
      id: listId
    });
  });
  const items = await new Promise((resolve, reject) => {
    const handleRequest = event => {
      const {
        data: eventData
      } = event;
      const {
        type,
        data
      } = eventData;
      if (type === messages.LOAD_LIST_ITEMS) {
        port.removeEventListener('message', handleRequest);
        resolve(data);
      } else {
        reject("Did not receive expected backend response!");
      }
    };
    port.onmessage = handleRequest;
    port.postMessage({
      type: messages.LOAD_LIST_ITEMS,
      id: listId,
      size: 100,
      sortType: DEFAULT_SORT_FIELDS.at(0),
      sortOrder: 'DESC'
    });
  });
  return {
    list,
    items,
    count
  };
};
const requestSharedList = async (id, pb) => {
  let list;
  try {
    list = await pb.collection('lists').getOne(id);
  } catch (error) {
    throw notFound();
  }

  // Otherwise attempt to load this list
  let items = [];
  try {
    const filter = pb.filter("list = {:id}", {
      id
    });
    items = await pb.collection('list_items').getFullList({
      filter,
      expand: 'item'
    });
  } catch (error) {
    console.warn(`No list items found for list ${id}`);
  }

  // Map expanded item data to this list item
  items = items.map(item => {
    return {
      ...item,
      ...item.expand.item,
      id: item.expand.item?.uri
    };
  });

  // Set the readonly flag and cast dates to Date()
  list.readonly = true;
  list.createdDate = new Date(list.createdDate);
  if (list.updatedDate) {
    list.updatedDate = new Date(list.updatedDate);
  }
  return {
    list,
    items
  };
};
export const Route = createFileRoute('/$listId')({
  component: ListItemsPage,
  beforeLoad: async () => {
    // NB: Overrides webpack 5's publicDir setting for the worker bundle
    __webpack_public_path__ = "/assets/";
    const storageWorker = new Worker(new URL('../../storageWorker/index.mjs', import.meta.url));
    const workerPort = await initializeWorker(storageWorker);
    const pb = new Pocketbase(backend);
    return {
      pb,
      storageWorker,
      workerPort
    };
  },
  loader: async ({
    context: {
      pb,
      storageWorker,
      workerPort
    },
    params
  }) => {
    const {
      listId
    } = params;
    const result = {
      listId,
      pb,
      storageWorker,
      workerPort
    };
    const initialData = await requestInitialData(workerPort, pb, listId);
    if (initialData.list?.shared) {
      initialData.list.sharedBy = true;
    }

    // Insert a property indicating this was shared by the user
    if (initialData.list) {
      initialData.list.sharedBy = initialData.list.shared;
      result.initialData = initialData;
      return result;
    }
    const {
      list,
      items
    } = await requestSharedList(listId, pb);
    result.initialData = {
      list,
      items,
      count: items.length
    };
    return result;
  },
  notFoundComponent: () => {
    return <section className="section list-page">
            <h2>List not found</h2>
            <p>A list at this address could not be found.</p>
            <p><a href="/list">Create a new list</a> or <a href="/">go back to the homepage</a>.</p>
          </section>;
  }
});
function ListItemsPage() {
  const {
    listId,
    initialData,
    storageWorker,
    workerPort,
    pb
  } = Route.useLoaderData();
  const origin = useMemo(() => window.location.origin, []);

  // List metadata
  const [count, setCount] = useState(initialData.count);
  const [name, setName] = useState(initialData.list.name);
  const [description, setDescription] = useState(initialData.list.description);
  const [listShared, setListShared] = useState(initialData.list.sharedBy);
  const [listReadOnly, setListReadOnly] = useState(initialData.list.readonly);
  const [createdDate, setCreatedDate] = useState(initialData.list.createdDate);
  const [updatedDate, setUpdatedDate] = useState(initialData.list.updatedDate);

  // Component load and UI state
  const [editing, setEditing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProcessing, setExportProcessing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [unsharing, setUnsharing] = useState(false);
  const [showExportImagesTab, setShowExportImagesTab] = useState(false);
  const [ready, setReady] = useState(false);

  // Data browse state -- page semantics, sorts
  const [pageIdx, setPageIdx] = useState(null);
  const [sortType, setSortType] = useState(DEFAULT_SORT_FIELDS.at(0));
  const [sortOrder, setSortOrder] = useState('DESC');

  // Display data
  const [data, setData] = useState(initialData.items);
  const createdFormatter = useMemo(() => new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }), []);
  const updatedFormatter = useMemo(() => new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
  }), []);

  // Storage cursor
  const [after, setAfter] = useState(null);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);

  // Input refs
  const nameElement = useRef(null);
  const descriptionElement = useRef(null);
  const formatsElement = useRef(null);

  // Ref to the list for printing
  const pdfElement = useRef(null);
  const table = useReactTable({
    data,
    columns: listItemColumns,
    getCoreRowModel: getCoreRowModel()
  });
  const broadcast = useMemo(() => new BroadcastChannel('cr-lists-storage'));

  /**
   * @function requestStorageData
   *
   * Sends the worker load requests
   *
   **/
  const requestStorageData = after => {
    workerPort.postMessage({
      type: messages.LOAD_LIST,
      id: listId
    });
    workerPort.postMessage({
      type: messages.COUNT_LIST_ITEMS,
      id: listId
    });
    workerPort.postMessage({
      type: messages.LOAD_LIST_ITEMS,
      id: listId,
      after,
      size,
      sortType,
      sortOrder
    });
  };
  const handleBroadcast = event => {
    const {
      data
    } = event;
    const {
      type
    } = data;
    switch (type) {
      case messages.STORAGE_UPDATED:
        requestStorageData();
        break;
      default:
        console.warn(`Unknown broadcast ${type}`);
    }
  };
  const messageHandler = event => {
    const {
      data: eventData
    } = event;
    const {
      type,
      data
    } = eventData;
    switch (type) {
      case messages.COUNT_LIST_ITEMS:
        setCount(data);
        break;
      case messages.LOAD_LIST:
        if (!data) return;
        const {
          name,
          description,
          createdDate,
          updatedDate,
          shared
        } = data;
        setName(name);
        setDescription(description);
        setCreatedDate(createdDate);
        setUpdatedDate(updatedDate);
        setListShared(shared);
        break;
      case messages.LOAD_LIST_ITEMS:
        const items = data;
        if (items.length <= 0) return;

        // NB: Null fallthrough in callback due to capture on the handler
        setPageIdx(pageIdx => pageIdx ?? 0);
        setData(items);
        break;
      case messages.SAVE_LIST:
        // TODO: ok, no-op -- consider using this to UI signal save finish
        break;
      default:
        console.warn(`Got an unrecognized message type, ${type}`);
        return;
    }
  };

  // Storage worker init: add listener hook
  useEffect(() => {
    setReady(true);
    workerPort.onmessage = messageHandler;
    forwardAuthorization(backend, workerPort);
  }, []);

  // Initialize list reload handler for shared lists
  useEffect(() => {
    if (!listReadOnly) return;
    pb.collection('lists').subscribe(listId, async function (e) {
      const {
        list,
        items
      } = await requestSharedList(listId, pb);
      const {
        name,
        description,
        createdDate,
        updatedDate,
        shared
      } = list;
      setName(name);
      setDescription(description);
      setCreatedDate(createdDate);
      setUpdatedDate(updatedDate);
      setListShared(shared);
      setData(items);
    });
  }, [listReadOnly, pb]);
  useEffect(() => {
    if (!ready) return;

    // Add event listener for custom update event
    broadcast.onmessage = handleBroadcast;
  }, [ready]);

  // Handler for changes in cursor state
  useEffect(() => {
    if (!ready) return;
    requestStorageData();
  }, [sortType, sortOrder]);

  /**
   * @function saveData
   *
   * @param {Object} list
   *
   * Saves the list data passed
   *
   **/
  const saveData = list => {
    workerPort.postMessage({
      type: messages.SAVE_LIST,
      list
    });
  };

  /**
   * @function removeItem
   *
   **/
  const removeItem = id => {
    const updated = data.filter(d => d.id !== id);
    workerPort.postMessage({
      type: messages.REMOVE_ITEM_FROM_LIST,
      id,
      fromId: listId
    });
  };
  const toggleEditing = event => {
    const changed = nameElement?.current && nameElement?.current.value !== name || descriptionElement?.current && descriptionElement?.current.value !== description;
    if (editing && changed) {
      const description = descriptionElement?.current.value;
      const name = nameElement?.current.value;
      saveData({
        id: listId,
        name,
        description,
        items: [],
        createdDate,
        updatedDate: new Date()
      });
      setName(name);
      setDescription(description);
    }

    // Clean up the inputs and close the modal
    if (descriptionElement.current && nameElement.current) {
      descriptionElement.current.value = description;
      nameElement.current.value = name;
    }
    setEditing(e => !e);
  };
  const firstPageHandler = event => {
    event.preventDefault();
    setPageIdx(0);
    requestStorageData();
  };
  const prevPageHandler = event => {
    event.preventDefault();
    if (pageIdx < 1) return;
    const after = (pageIdx - 1) * size;
    setPageIdx(pageIdx - 1);
    requestStorageData(after);
  };
  const nextPageHandler = event => {
    event.preventDefault();
    const after = (pageIdx + 1) * size;
    if (after >= count) return;
    setPageIdx(pageIdx + 1);
    requestStorageData(after);
  };
  const lastPageHandler = event => {
    event.preventDefault();
    const lastPage = count % size === 0 ? count / size - 1 : Math.floor(count / size);
    const after = lastPage * size;
    setPageIdx(lastPage);
    requestStorageData(after);
  };
  const gotoPageHandler = (event, p) => {
    event.preventDefault();

    // Convert page label to index
    const i = p - 1;
    const after = i * size;
    if (after >= count) return;
    setPageIdx(p);
    requestStorageData(after);
  };
  const changeSortType = type => {
    setSortType(type);
  };
  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
  };
  const onClickToggleExporting = () => {
    setExporting(!exporting);
  };
  const onClickToggleSharing = () => {
    setSharing(!sharing);
  };
  const onClickToggleUnsharing = () => {
    setUnsharing(!unsharing);
  };
  const toggleExportImagesTab = event => {
    event.preventDefault();
    setShowExportImagesTab(!showExportImagesTab);
  };
  const exportPDF = async options => {
    const printer = new DataPrinter();
    let updated;
    if (Boolean(updatedDate)) updated = updatedFormatter.format(updatedDate);
    await printer.print(name, description, createdFormatter.format(createdDate), updated, data, options);
  };
  const exportCSV = async options => {
    let updated;
    if (Boolean(updatedDate)) updated = updatedFormatter.format(updatedDate);
    const printer = new DataPrinter();
    await printer.print(name, description, createdFormatter.format(createdDate), updated, data, options);
  };
  const exportJSON = async options => {
    const printer = new DataPrinter();
    let updated;
    if (Boolean(updatedDate)) updated = updatedFormatter.format(updatedDate);
    await printer.print(name, description, createdFormatter.format(createdDate), updated, data, options);
  };

  // Handler for passing to export modal
  const handleExport = async (format, options) => {
    setExportProcessing(true);
    switch (format) {
      case 'CSV':
      case 'CSV-BOM':
        await exportCSV(options);
        break;
      case 'JSON':
        await exportJSON(options);
        break;
      case 'PDF':
        await exportPDF(options);
        break;
      default:
        console.warn(`Received unknown format ${format}`);
    }
    setExportProcessing(false);
    setExporting(false);
  };
  const handleShare = async () => {
    saveData({
      id: listId,
      name,
      description,
      createdDate,
      updatedDate: new Date(),
      shared: true
    });
    setSharing(false);
  };
  const handleUnshare = async () => {
    saveData({
      id: listId,
      name,
      description,
      createdDate,
      updatedDate: new Date(),
      shared: false
    });
    setUnsharing(false);
  };
  return <section ref={pdfElement} className="section list-page">
            <h2>{name}
              <span className={`tag is-read-only ${!listReadOnly ? 'is-hidden' : ''}`}>Read Only</span>
              <span className={`tag is-shared ${!listShared ? 'is-hidden' : ''}`}>Shared</span>
            </h2>
            <p className={!listShared ? 'is-hidden' : ''}><a href={`${origin}/list/${listId}`}>{origin}/list/{listId}</a><button className="btn btn-text" onClick={async () => await navigator.clipboard.writeText(`${origin}/list/${listId}`)}>Copy URL</button></p>
            <p className="my-2 intro-text">{description}</p>
            <div className="list-info border-bottom mt-4 pb-3">
              <div className="left-column">
                <div className="left-column-div">
                  <span>Created {createdFormatter.format(createdDate)}</span>
                  <div className="vr"></div>
                  <span className={updatedDate ? '' : 'is-hidden'}>Updated {updatedFormatter.format(updatedDate)}</span>
                  <div className="vr is-hidden-mobile d-none-print"></div>
                </div>
                <div className="left-column-div">
                  <button className={`btn-text ${listReadOnly ? 'is-hidden' : ''}`} onClick={toggleEditing}>{editing ? 'Stop Editing' : 'Edit'} List Details</button>
                </div>
              </div>
              <div className="right-column">
                <button type="button" className={`button ${listReadOnly || !pb?.authStore?.isValid || listShared ? 'is-hidden' : ''}`} onClick={onClickToggleSharing}><span className="icon icon-share-list"></span>Share List</button>
                    {/* !listShared || !pb?.authStore?.isValid ? 'is-hidden' : '' */}
                <button type="button" className={`button is-hidden`} onClick={onClickToggleUnsharing}><span className="icon icon-share-list"></span>Stop Sharing</button>
                <button type="button" className="button" onClick={onClickToggleExporting}>
                  <span className="icon icon-download"></span>Export List</button>
              </div>
            </div>
            <div className="list-sort">
              <span className={`${count !== null ? '' : 'is-hidden'}`}>{count > 1 || count === 0 ? `${count} Items` : `${count} Item`}</span>
              <div className="is-flex ml-auto is-flex-direction-column">
                <div className="list-sort-div">
                  <button className="btn-text is-hidden">Reorder</button>
                  <div className="vr"></div>
                  <div className="sort-button">Sort
                    <SortControl sorts={DEFAULT_SORT_FIELDS} callback={changeSortType} />
                    <button id="search-interface-sort-toggle" className={`button icon-button ml-1 ${listReadOnly ? 'is-hidden' : ''}`} title={`Sort results`} onClick={toggleSortOrder}>
                      <img src="/assets/images/icon-sortdown.png" alt="sort" style={sortOrder === 'ASC' ? {
                transform: 'rotate(180deg)'
              } : {}} />
                    </button>
                  </div>
                </div>
                <Pagination count={count} size={size} pageIndex={pageIdx} firstPage={firstPageHandler} prevPage={prevPageHandler} nextPage={nextPageHandler} lastPage={lastPageHandler} gotoPage={gotoPageHandler} />
              </div>
            </div>

            <ul className="list">
              {table.getRowModel().rows.map((row, i) => ListItemRow(row, i, removeItem))}
            </ul>
            <div id="edit-list-modal" className={`modal ${editing ? 'is-active' : ''}`}>
              <div className="modal-background"></div>
              <div className="modal-card">
                <header className="modal-card-head">
                  <h1 className="modal-card-title">
                    EDIT LIST</h1>
                  <button onClick={() => setEditing(false)} className="btn-icon-only icon-lg btn-close" aria-label="close">
                    <span className="icon icon-close-large"></span><span className="sr-only">Close this
                      dialog</span>
                  </button>
                </header>
                <section className="modal-card-body">
                  <div className="field">
                    <label className="label">List Name:</label>
                    <div className="control">
                      <input defaultValue={name} ref={nameElement} className="input" type="text" placeholder="List Name" />
                    </div>
                  </div>
                  <div className="field">
                    <label className="label">List Description:</label>
                    <div className="control">
                      <input ref={descriptionElement} defaultValue={description} className="input" type="text" placeholder="List Description" />
                    </div>
                  </div>
                </section>
                <footer className="modal-card-foot">
                  <div className="is-flex ml-auto">
                    <button className="button" onClick={() => setEditing(false)}>Cancel</button>
                    <button className="btn-solid" onClick={toggleEditing}>Save</button>
                  </div>
                </footer>
              </div>
            </div>
            <div id="share-modal" className="modal">
              <div className="modal-background"></div>
              <div className="modal-card">
                <header className="modal-card-head">
                  <h1 className="modal-card-title">
                    Share this List</h1>
                  <button className="btn-icon-only icon-lg btn-close" aria-label="close">
                    <span className="icon icon-close-large"></span><span className="sr-only">Close this
                      dialog</span>
                  </button>
                </header>
                <section className="modal-card-body">
                  <p>When you share this list, a public link to the list will be created. You can copy this link and send it to
                    anyone you choose. They will be able to view, but not edit, the list.

                  </p>
                  <p>In the future, you can stop sharing the list, which will delete the public link, and prevent others from
                    accessing it.</p>
                  <p>List Name: <br /><span>Display the name of the list here</span></p>
                </section>
                <footer className="modal-card-foot">
                  <div className="is-flex ml-auto">
                    <button className="button">Cancel</button>
                    <button className="btn-solid">Share</button>
                  </div>
                </footer>
              </div>
            </div>
            <UnshareModal unsharing={unsharing} cancel={onClickToggleUnsharing} handleUnshare={handleUnshare} />

            <ShareModal sharing={sharing} cancel={onClickToggleSharing} name={name} handleShare={handleShare} />
            <ExportModal showModal={exporting} exporting={exportProcessing} cancel={onClickToggleExporting} handleExport={handleExport} />
          </section>;
}
if (import.meta.hot) {
  import.meta.hot.accept(newModule => {
    if (newModule && newModule.Route && typeof newModule.Route.clone === 'function') {
      newModule.Route.clone(Route);
    }
  });
}